import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { HeaderService, UnidadOperativaItem } from '../../services/header.service';
import { AuthStore, ID_JERARQUIA_TODAS } from '../../../store/auth.store';
import {
  ActividadGrupal,
  ActividadGrupalCreate,
  ActividadGrupalService,
  ActividadGrupalUpdate,
  CategoriaActividadGrupalResponse
} from '../../services/actividad-grupal.service';
import { ToastService } from '../../services/toast.service';
import { calcularIdJerarquiaParaCargaUO } from '../../utils/perfil.util';
import { VisitasService, ObjetivoVisita } from '../../services/visitas.service';

type UnidadOperativaOption = { id: number; nombre: string };
type CategoriaAsesoriaOption = { value: string; label: string };
export type AccionActividadGrupal = 'crear' | 'editar';

@Component({
  selector: 'app-formulario-actividad-grupal',
  templateUrl: './formulario-actividad-grupal.component.html',
  styleUrls: ['./formulario-actividad-grupal.component.scss'],
  standalone: false,
})
export class FormularioActividadGrupalComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly ID_CATEGORIA_CON_ESPECIALISTA = 1;

  @Input() visible: boolean = false;
  /** Cuando true, el contenido se muestra dentro de un ion-menu (sin overlay ni panel fijo). */
  @Input() dentroDeMenu: boolean = false;
  @Input() accion: AccionActividadGrupal = 'crear';
  @Input() actividad: ActividadGrupal | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() guardado = new EventEmitter<void>();

  unidadesOperativas: UnidadOperativaOption[] = [];
  categoriasAsesoriaOpciones: CategoriaAsesoriaOption[] = [];
  objetivosVisita: ObjetivoVisita[] = [];

  // Fechas y horas (para ion-datetime)
  fechaInicioISO: string | null = null;
  horaInicioHHmm: string | null = null;
  fechaISO: string | null = null;
  horaHHmm: string | null = null;
  horaProgramadaHHmm: string | null = null;

  formulario: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly authStore = inject(AuthStore);
  private readonly actividadGrupalService = inject(ActividadGrupalService);
  private readonly visitasService = inject(VisitasService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subs = new Subscription();

  guardando = false;
  private categoriaLabelPendiente: string | null = null;
  private unidadOperativaIdPendiente: number | null = null;

  constructor() {
    this.formulario = this.fb.group({
      id_unidad_operativa: [ID_JERARQUIA_TODAS, Validators.required],
      categoria: ['', Validators.required],
      asesor_especialista: [''],
      nombre_asesoria: ['', Validators.required],
      objetivos: ['', Validators.required],
      lugar: [''],
      fecha_inicio: [''],
      hora_inicio: [''],
      fecha: [''],
      hora: [''],
      notificar_ejecutivos: [false],
      id_objetivo_visita: [[], Validators.required],
      objetivo_otro: ['']
    });
  }

  ngOnInit(): void {
    const vSub = this.formulario.valueChanges?.subscribe(() => this.cdr.markForCheck());
    if (vSub) this.subs.add(vSub);
    const sSub = this.formulario.statusChanges?.subscribe(() => this.cdr.markForCheck());
    if (sSub) this.subs.add(sSub);

    const catSub = this.formulario
      .get('categoria')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => {
        this.aplicarValidadorAsesorEspecialista();
        this.cdr.markForCheck();
      });
    if (catSub) this.subs.add(catSub);

    const notifSub = this.formulario
      .get('notificar_ejecutivos')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((notificar) => {
        this.actualizarValidadoresNotificacion(notificar);
      });
    if (notifSub) this.subs.add(notifSub);

    const objetivoSub = this.formulario
      .get('id_objetivo_visita')
      ?.valueChanges.subscribe((ids: unknown) => {
        const ctrl = this.formulario.get('objetivo_otro');
        if (!ctrl) return;
        if (Array.isArray(ids) && ids.includes(13)) {
          ctrl.setValidators([Validators.required]);
        } else {
          ctrl.clearValidators();
          ctrl.setValue('', { emitEvent: false });
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      });
    if (objetivoSub) this.subs.add(objetivoSub);

    // Aplicar validadores iniciales para detectar si ya hay objetivo 13 seleccionado al cargar
    const idsIniciales = this.formulario.get('id_objetivo_visita')?.value;
    if (Array.isArray(idsIniciales) && idsIniciales.includes(13)) {
      this.formulario.get('objetivo_otro')?.setValidators([Validators.required]);
      this.formulario.get('objetivo_otro')?.updateValueAndValidity({ emitEvent: false });
    }

    this.cargarCategoriasActividadGrupal();
    this.cargarObjetivosVisita();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.inicializarFormulario();
      this.cargarValoresParaAccion();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetearFormulario();
  }

  onOverlayTouchend(event: TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cerrar();
  }

  getTitulo(): string {
    return this.accion === 'editar' ? 'Editar Actividad Grupal' : 'Crear Actividad Grupal';
  }

  getSubtitulo(): string {
    return this.accion === 'editar'
      ? 'Actualice los datos de la actividad grupal.'
      : 'Complete el formulario para programar una nueva actividad grupal.';
  }

  requiereAsesorEspecialista(): boolean {
    const categoria = this.formulario.get('categoria')?.value;
    return Number(categoria) === FormularioActividadGrupalComponent.ID_CATEGORIA_CON_ESPECIALISTA;
  }

  actualizarValidadoresNotificacion(notificar: boolean): void {
    const lugarCtrl = this.formulario.get('lugar');
    const horaCtrl = this.formulario.get('hora');
    const horaInicioCtrl = this.formulario.get('hora_inicio');

    if (notificar) {
      lugarCtrl?.setValidators([Validators.required]);
      horaCtrl?.setValidators([Validators.required]);
      horaInicioCtrl?.setValidators([Validators.required]);
    } else {
      lugarCtrl?.clearValidators();
      horaCtrl?.clearValidators();
      horaInicioCtrl?.clearValidators();
    }

    lugarCtrl?.updateValueAndValidity({ emitEvent: false });
    horaCtrl?.updateValueAndValidity({ emitEvent: false });
    horaInicioCtrl?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  esFormularioValido(): boolean {
    const raw = this.formulario.getRawValue();
    const asesorOk =
      !this.requiereAsesorEspecialista() ||
      (raw.asesor_especialista != null && String(raw.asesor_especialista).trim() !== '');

    const notificar = raw.notificar_ejecutivos === true;
    const lugarOk = !notificar || (raw.lugar != null && String(raw.lugar).trim() !== '');
    const horaOk = !notificar || (!!this.horaHHmm && !!this.horaInicioHHmm);

    const fechaInicioStr = this.fechaInicioISO ? this.fechaInicioISO.split('T')[0] : '';
    const fechaFinStr = this.fechaISO ? this.fechaISO.split('T')[0] : '';
    const rangoFechasOk = !!fechaInicioStr && !!fechaFinStr && fechaFinStr >= fechaInicioStr;

    const requeridos =
      raw.id_unidad_operativa != null &&
      raw.categoria != null &&
      String(raw.categoria).trim() !== '' &&
      asesorOk &&
      raw.nombre_asesoria != null &&
      String(raw.nombre_asesoria).trim() !== '' &&
      raw.objetivos != null &&
      String(raw.objetivos).trim() !== '' &&
      raw.id_objetivo_visita != null &&
      raw.id_objetivo_visita.length > 0 &&
      lugarOk &&
      horaOk &&
      rangoFechasOk &&
      !this.esFechaCalendarioAnteriorAHoy(this.fechaInicioISO!) &&
      this.formulario.valid;

    if (!requeridos) return false;

    // Requerimiento: no permitir guardar si se dejó seleccionada la opción genérica "Todas las Unidades Operativas".
    if (Number(raw.id_unidad_operativa) === ID_JERARQUIA_TODAS) {
      return false;
    }

    return true;
  }

  onGuardarClick(): void {
    void this.guardar();
  }

  async guardar(): Promise<void> {
    if (this.guardando) return;

    if (!this.esFormularioValido()) {
      await this.toastService.mostrar('Por favor complete todos los campos requeridos', 'warning');
      return;
    }

    const raw = this.formulario.getRawValue();
    const fechaHora = this.formatearFechaHora(this.fechaISO!, this.horaHHmm || '00:00');
    const fechaInicioHora = this.formatearFechaHora(this.fechaInicioISO!, this.horaInicioHHmm || '00:00');

    if (!fechaHora || !fechaInicioHora) {
      await this.toastService.mostrar('Error: fecha y hora inválidas', 'error');
      return;
    }

    const asesorTrim = String(raw.asesor_especialista ?? '').trim();
    const payloadBase = {
      nombre: String(raw.nombre_asesoria).trim(),
      objetivos: String(raw.objetivos).trim(),
      asesor: asesorTrim,
      lugar: String(raw.lugar ?? '').trim(),
      fecha: fechaHora,
      fecha_inicio: fechaInicioHora,
      notificar_ejecutivos: raw.notificar_ejecutivos === true,
      id_objetivo_visita: raw.id_objetivo_visita || [],
      objetivo_otro: raw.objetivo_otro?.trim() || null,
      hora_programada: this.horaProgramadaHHmm
    };

    this.guardando = true;
    try {
      if (this.accion === 'crear') {
        const payload: ActividadGrupalCreate = {
          id_jerarquia: Number(raw.id_unidad_operativa),
          id_categoria_asesoria_especializada: Number(raw.categoria),
          ...payloadBase
        };

        await firstValueFrom(this.actividadGrupalService.crearActividadGrupal(payload));
        await this.toastService.mostrar('Actividad creada correctamente', 'success');
      } else {
        if (!this.actividad?.id_asesoria_especializada) {
          await this.toastService.mostrar('Error: no se pudo identificar la actividad a editar', 'error');
          return;
        }

        const payload: ActividadGrupalUpdate = {
          id_categoria_asesoria_especializada: Number(raw.categoria),
          id_jerarquia: Number(raw.id_unidad_operativa),
          nombre: payloadBase.nombre,
          objetivos: payloadBase.objetivos,
          asesor: this.requiereAsesorEspecialista() ? payloadBase.asesor : (asesorTrim || null),
          lugar: payloadBase.lugar,
          fecha: payloadBase.fecha,
          fecha_inicio: payloadBase.fecha_inicio,
          notificar_ejecutivos: payloadBase.notificar_ejecutivos,
          id_objetivo_visita: payloadBase.id_objetivo_visita,
          objetivo_otro: payloadBase.objetivo_otro,
          hora_programada: payloadBase.hora_programada
        };

        await firstValueFrom(
          this.actividadGrupalService.editarActividadGrupal(this.actividad.id_asesoria_especializada, payload)
        );
        await this.toastService.mostrar('Actividad actualizada correctamente', 'success');
      }

      this.guardado.emit();
      this.cerrar();
    } catch (error: any) {
      const mensaje =
        error?.error?.message ?? (typeof error?.error === 'string' ? error.error : null) ?? 'Error al realizar la operación';
      await this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.guardando = false;
    }
  }

  onFechaInicioChange(event: any): void {
    const val = event?.detail?.value;
    if (val) {
      const str = String(val);
      const fecha = this.esFechaCalendarioAnteriorAHoy(str) ? this.obtenerFechaHoyParaDatetime() : str;
      this.fechaInicioISO = fecha;
      this.formulario.patchValue({ fecha_inicio: fecha });

      if (this.fechaISO && this.fechaISO.split('T')[0] < fecha.split('T')[0]) {
        this.fechaISO = fecha;
        this.formulario.patchValue({ fecha });
      }
    } else {
      this.fechaInicioISO = null;
      this.formulario.patchValue({ fecha_inicio: '' });
    }
    this.cdr.markForCheck();
  }

  onHoraInicioChange(event: any): void {
    const val = event?.detail?.value;
    if (val) {
      const hora = typeof val === 'string' && val.includes('T') ? this.extraerHoraDeISO(val) : String(val);
      this.horaInicioHHmm = hora;
      this.formulario.patchValue({ hora_inicio: hora });
    } else {
      this.horaInicioHHmm = null;
      this.formulario.patchValue({ hora_inicio: '' });
    }
    this.cdr.markForCheck();
  }

  onFechaChange(event: any): void {
    const val = event?.detail?.value;
    if (val) {
      const str = String(val);
      const minDate = this.fechaInicioISO ? this.fechaInicioISO.split('T')[0] : '';
      const selectedDate = str.split('T')[0];
      const fecha = minDate && selectedDate < minDate ? this.fechaInicioISO! : str;
      this.fechaISO = fecha;
      this.formulario.patchValue({ fecha });
    } else {
      this.fechaISO = null;
      this.formulario.patchValue({ fecha: '' });
    }
    this.cdr.markForCheck();
  }

  onHoraChange(event: any): void {
    const val = event?.detail?.value;
    if (val) {
      const hora = typeof val === 'string' && val.includes('T') ? this.extraerHoraDeISO(val) : String(val);
      this.horaHHmm = hora;
      this.formulario.patchValue({ hora });
    } else {
      this.horaHHmm = null;
      this.formulario.patchValue({ hora: '' });
    }
    this.cdr.markForCheck();
  }

  onHoraProgramadaChange(event: any): void {
    const val = event?.detail?.value;
    if (val) {
      const hora = typeof val === 'string' && val.includes('T') ? this.extraerHoraDeISO(val) : String(val);
      this.horaProgramadaHHmm = hora;
      this.horaInicioHHmm = hora;
      this.horaHHmm = hora;
      this.formulario.patchValue({
        hora_inicio: hora,
        hora: hora
      });
    } else {
      this.horaProgramadaHHmm = null;
      this.horaInicioHHmm = null;
      this.horaHHmm = null;
      this.formulario.patchValue({
        hora_inicio: '',
        hora: ''
      });
    }
    this.cdr.markForCheck();
  }

  private extraerHoraDeISO(iso: string): string {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** ISO para ion-datetime (fecha local de hoy a mediodía, evita corrimientos por zona horaria). */
  private obtenerFechaHoyParaDatetime(): string {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0).toISOString();
  }

  /** Límite inferior del calendario: inicio del día local (alineado con formulario-visita). */
  getFechaMinimaSeleccionISO(): string {
    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return inicioHoy.toISOString();
  }

  private esFechaCalendarioAnteriorAHoy(isoVal: string): boolean {
    const d = new Date(isoVal);
    if (Number.isNaN(d.getTime())) return false;
    const hoy = new Date();
    const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const h0 = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).getTime();
    return d0 < h0;
  }

  private formatearFechaHora(fechaISO: string, horaHHmm: string): string {
    if (!fechaISO || !horaHHmm) return '';
    const fecha = new Date(fechaISO);

    const horaStr = String(horaHHmm).trim();
    const match = horaStr.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM|am|pm))?$/);
    if (!match) {
      return fecha.toISOString();
    }

    let hh = Number(match[1]);
    const mm = Number(match[2]);
    const ampm = match[3]?.toUpperCase();

    if (ampm) {
      if (ampm === 'PM' && hh < 12) hh += 12;
      if (ampm === 'AM' && hh === 12) hh = 0;
    }

    fecha.setHours(hh, mm, 0, 0);
    return fecha.toISOString();
  }

  private cargarCategoriasActividadGrupal(): void {
    if (this.categoriasAsesoriaOpciones.length > 0) return;

    const s = this.actividadGrupalService.obtenerCategoriasActividadGrupal().subscribe({
      next: (resp: CategoriaActividadGrupalResponse[]) => {
        this.categoriasAsesoriaOpciones = (resp ?? [])
          .map(item => {
            return item?.id_categoria_asesoria_especializada != null
              ? {
                value: String(item.id_categoria_asesoria_especializada),
                label: item.descripcion || String(item.id_categoria_asesoria_especializada)
              }
              : null;
          })
          .filter((x): x is CategoriaAsesoriaOption => x != null);

        // Si venimos en modo edición, intentar aplicar la categoría en cuanto lleguen los datos.
        if (this.categoriaLabelPendiente) {
          const opt = this.categoriasAsesoriaOpciones.find(o => o.label === this.categoriaLabelPendiente);
          if (opt) {
            this.formulario.patchValue({ categoria: opt.value }, { emitEvent: false });
          }
          this.categoriaLabelPendiente = null;
        }

        this.aplicarValidadorAsesorEspecialista();
        this.cdr.markForCheck();
      },
      error: () => {
        this.categoriasAsesoriaOpciones = [];
        this.cdr.markForCheck();
      }
    });

    this.subs.add(s);
  }

  private cargarObjetivosVisita(): void {
    this.visitasService.obtenerObjetivosVisita().subscribe({
      next: (list) => {
        this.objetivosVisita = Array.isArray(list) ? list : [];
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error al cargar objetivos para temas:', err);
        this.objetivosVisita = [];
        this.cdr.markForCheck();
      }
    });
  }

  private cargarValoresParaAccion(): void {
    if (this.accion === 'editar' && this.actividad) {
      this.unidadOperativaIdPendiente = this.actividad.id_jerarquia ?? null;
      this.formulario.patchValue(
        {
          asesor_especialista: this.actividad.asesor ?? '',
          nombre_asesoria: this.actividad.nombre ?? '',
          objetivos: this.actividad.objetivos ?? '',
          lugar: this.actividad.lugar ?? '',
          notificar_ejecutivos: false,
          id_objetivo_visita: this.actividad.id_objetivo_visita ?? [],
          objetivo_otro: this.actividad.objetivo_otro ?? ''
        },
        { emitEvent: false }
      );

      let horaProg = this.actividad.detalle?.hora_programada || null;
      if (!horaProg && this.actividad.fecha) {
        // Fallback: Try regex match on formatted string range first (since legacy/prod backend returns fecha as range string)
        const matches = this.actividad.fecha.match(/\b\d{1,2}:\d{2}\b/g);
        if (matches && matches.length > 0) {
          const lastTime = matches[matches.length - 1];
          if (lastTime !== '00:00') {
            // Pad hour to HH:mm
            const parts = lastTime.split(':');
            horaProg = `${parts[0].padStart(2, '0')}:${parts[1]}`;
          }
        } else {
          const d = new Date(this.actividad.fecha);
          if (!isNaN(d.getTime())) {
            const hh = d.getHours();
            const mm = d.getMinutes();
            if (hh !== 0 || mm !== 0) {
              horaProg = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
            }
          }
        }
      }
      this.horaProgramadaHHmm = horaProg;
      this.horaInicioHHmm = horaProg;
      this.horaHHmm = horaProg;

      if (this.actividad.fecha_inicio) {
        const isoOriginal = new Date(this.actividad.fecha_inicio).toISOString();
        let iso = isoOriginal;
        if (this.esFechaCalendarioAnteriorAHoy(iso)) {
          iso = this.obtenerFechaHoyParaDatetime();
        }
        this.fechaInicioISO = iso;
        this.formulario.patchValue({ fecha_inicio: iso, hora_inicio: horaProg || '' }, { emitEvent: false });
      } else {
        const hoy = this.obtenerFechaHoyParaDatetime();
        this.fechaInicioISO = hoy;
        this.formulario.patchValue({ fecha_inicio: hoy, hora_inicio: '' }, { emitEvent: false });
      }

      const fechaTerminoVal = this.actividad.fecha_termino || this.actividad.fecha;
      if (fechaTerminoVal) {
        const isoOriginal = new Date(fechaTerminoVal).toISOString();
        let iso = isoOriginal;
        if (this.esFechaCalendarioAnteriorAHoy(iso)) {
          iso = this.obtenerFechaHoyParaDatetime();
        }
        this.fechaISO = iso;
        this.formulario.patchValue({ fecha: iso, hora: horaProg || '' }, { emitEvent: false });
      } else {
        const hoy = this.obtenerFechaHoyParaDatetime();
        this.fechaISO = hoy;
        this.formulario.patchValue({ fecha: hoy, hora: '' }, { emitEvent: false });
      }

      const categoriaLabel = this.actividad.categoria_descripcion || this.actividad.categoria;
      if (categoriaLabel) {
        const opt = this.categoriasAsesoriaOpciones.find(o => o.label === categoriaLabel);
        if (opt) {
          this.formulario.patchValue({ categoria: opt.value }, { emitEvent: false });
        } else {
          this.categoriaLabelPendiente = categoriaLabel;
        }
      }

      this.actualizarValidadoresNotificacion(this.actividad.notificar_ejecutivos ?? false);
    }

    this.aplicarValidadorAsesorEspecialista();
  }

  private aplicarValidadorAsesorEspecialista(): void {
    const ctrl = this.formulario.get('asesor_especialista');
    if (!ctrl) return;
    if (this.requiereAsesorEspecialista()) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private inicializarFormulario(): void {
    this.resetearFormulario();
    this.cdr.markForCheck();
  }

  private resetearFormulario(): void {
    const fechaHoy = this.obtenerFechaHoyParaDatetime();
    this.formulario.reset({
      id_unidad_operativa: ID_JERARQUIA_TODAS,
      categoria: '',
      asesor_especialista: '',
      nombre_asesoria: '',
      objetivos: '',
      lugar: '',
      fecha_inicio: fechaHoy,
      hora_inicio: '',
      fecha: fechaHoy,
      hora: '',
      notificar_ejecutivos: false,
      id_objetivo_visita: [],
      objetivo_otro: ''
    });
    this.formulario.enable({ emitEvent: false });
    this.categoriaLabelPendiente = null;
    this.unidadOperativaIdPendiente = null;
    this.fechaInicioISO = fechaHoy;
    this.horaInicioHHmm = null;
    this.fechaISO = fechaHoy;
    this.horaHHmm = null;
    this.horaProgramadaHHmm = null;
    this.aplicarValidadorAsesorEspecialista();
    this.actualizarValidadoresNotificacion(false);
    this.cargarUnidadesOperativas();
  }

  private cargarUnidadesOperativas(): void {
    const perfil = this.authStore.obtenerPerfilActivo();
    const unidadHeader = this.authStore.obtenerUnidadOperativaActiva();
    const idArea = calcularIdJerarquiaParaCargaUO(
      perfil,
      this.authStore.obtenerIdRegionSeleccionada(),
      this.authStore.obtenerIdAreaSeleccionada()
    );

    if (perfil?.id_perfil == null) {
      this.unidadesOperativas = [{ id: ID_JERARQUIA_TODAS, nombre: 'Todas las Unidades Operativas' }];
      this.formulario.patchValue({ id_unidad_operativa: ID_JERARQUIA_TODAS }, { emitEvent: false });
      this.formulario.get('id_unidad_operativa')?.disable({ emitEvent: false });
      return;
    }

    this.headerService.obtenerUnidadesOperativas(perfil.id_perfil, idArea).subscribe({
      next: (lista: UnidadOperativaItem[]) => {
        const unidadesApi = (Array.isArray(lista) ? lista : []).map(u => ({ id: u.id_jerarquia, nombre: u.descripcion }));

        if (unidadesApi.length === 0) {
          this.unidadesOperativas = [{ id: ID_JERARQUIA_TODAS, nombre: 'Todas las Unidades Operativas' }];
          this.formulario.patchValue({ id_unidad_operativa: ID_JERARQUIA_TODAS }, { emitEvent: false });
          this.formulario.get('id_unidad_operativa')?.disable({ emitEvent: false });
          this.unidadOperativaIdPendiente = null;
          this.cdr.markForCheck();
          return;
        }

        if (unidadesApi.length === 1) {
          this.unidadesOperativas = unidadesApi;
          const seleccionada = this.unidadOperativaIdPendiente != null ? this.unidadOperativaIdPendiente : unidadesApi[0].id;
          const idFinal = unidadesApi.some(u => u.id === seleccionada) ? seleccionada : unidadesApi[0].id;
          this.formulario.patchValue({ id_unidad_operativa: idFinal }, { emitEvent: false });
          this.formulario.get('id_unidad_operativa')?.disable({ emitEvent: false });
          this.unidadOperativaIdPendiente = null;
          this.cdr.markForCheck();
          return;
        }

        this.unidadesOperativas = [{ id: ID_JERARQUIA_TODAS, nombre: 'Todas las Unidades Operativas' }, ...unidadesApi];

        if (unidadHeader !== ID_JERARQUIA_TODAS) {
          const existe = unidadesApi.some(u => u.id === unidadHeader);
          const idFinal = existe ? unidadHeader : ID_JERARQUIA_TODAS;
          this.formulario.patchValue({ id_unidad_operativa: idFinal }, { emitEvent: false });
          this.formulario.get('id_unidad_operativa')?.disable({ emitEvent: false });
        } else {
          this.formulario.patchValue({ id_unidad_operativa: ID_JERARQUIA_TODAS }, { emitEvent: false });
          this.formulario.get('id_unidad_operativa')?.enable({ emitEvent: false });
        }

        if (this.unidadOperativaIdPendiente != null) {
          const idPendiente = this.unidadOperativaIdPendiente;
          const existe = unidadesApi.some(u => u.id === idPendiente);
          const idFinalPendiente = existe ? idPendiente : ID_JERARQUIA_TODAS;
          this.formulario.patchValue({ id_unidad_operativa: idFinalPendiente }, { emitEvent: false });
          this.unidadOperativaIdPendiente = null;
        }

        this.cdr.markForCheck();
      },
      error: () => {
        this.unidadesOperativas = [{ id: ID_JERARQUIA_TODAS, nombre: 'Todas las Unidades Operativas' }];
        this.formulario.patchValue({ id_unidad_operativa: ID_JERARQUIA_TODAS }, { emitEvent: false });
        if (unidadHeader !== ID_JERARQUIA_TODAS) {
          this.formulario.get('id_unidad_operativa')?.disable({ emitEvent: false });
        } else {
          this.formulario.get('id_unidad_operativa')?.enable({ emitEvent: false });
        }
        this.unidadOperativaIdPendiente = null;
        this.cdr.markForCheck();
      },
    });
  }

  formatearHoraParaMostrar(hora: string | null): string {
    if (!hora) return '';
    try {
      const parts = hora.split(':');
      if (parts.length < 2) return hora;
      let hh = parseInt(parts[0], 10);
      const mm = parts[1];
      const ampm = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12;
      hh = hh ? hh : 12;
      return `${hh}:${mm} ${ampm}`;
    } catch {
      return hora;
    }
  }

  limpiarHoraProgramada(): void {
    this.horaProgramadaHHmm = null;
    this.horaInicioHHmm = null;
    this.horaHHmm = null;
    this.formulario.patchValue({
      hora_inicio: '',
      hora: ''
    });
    this.cdr.markForCheck();
  }
}
